import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_GET_AUTO_PROCESSABLE_ORDERS,
  SR_API_ACTION_AUTOMATED_ORDER_PLACER_PLACE_ORDER,
} from 'api/utilityActionTypes';

import { AUTOMATED_ORDER_PLACER_TYPE_ATT } from 'common/utility/constants';

import * as AttController from 'client/components/UPOrderPlacingForm/attController';
import ImageGallery from 'client/components/UPOrderPlacingForm/ImageGallery';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFormikField, { SR_FIELD_THEME_ADMIN } from 'client/components/SrFormikField';
import ButtonDeprecated, { BUTTON_THEME_YELLOW } from 'client/components/ButtonDeprecated';

const getFormFields = ({ options, handleSelectChange, automatedOrderPlacerType }) => {
  switch (automatedOrderPlacerType) {
    case AUTOMATED_ORDER_PLACER_TYPE_ATT:
      return AttController.getFormFields({ options, handleSelectChange });
    default:
      return [];
  }
};

const buildPayload = ({ formValues, automatedOrderPlacerType }) => {
  switch (automatedOrderPlacerType) {
    case AUTOMATED_ORDER_PLACER_TYPE_ATT:
      return AttController.buildOrderPayload(formValues);
    default:
      return null;
  }
};

const handleSelectedActivation = ({
  setFieldValue,
  selectedActivation,
  automatedOrderPlacerType,
}) => {
  switch (automatedOrderPlacerType) {
    case AUTOMATED_ORDER_PLACER_TYPE_ATT:
      return AttController.handleOnSelectChange({ setFieldValue, selectedActivation });
    default:
      return null;
  }
};

const getTitle = (automatedOrderPlacerType) => {
  switch (automatedOrderPlacerType) {
    case AUTOMATED_ORDER_PLACER_TYPE_ATT:
      return 'Place AT&T Order';
    default:
      return 'Place Order';
  }
};

const sectionTitles = {
  'address.line1': 'Address',
  'previousAddress.line1': 'Previous Address',
  'paymentDetails.firstName': 'Payment Details',
  'paymentDetails.billingAddress.line1': 'Billing Address',
};

const UPOrderPlacingForm = ({ onClose, automatedOrderPlacerType }) => {
  const [activationOptions, setActivationOptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_ACTIVATION_GET_AUTO_PROCESSABLE_ORDERS,
          payload: {
            automatedOrderPlacerType,
          },
        });

        const options = response.map((activation) => ({
          label: [
            `ID: ${activation.activationShortId}`,
            `${activation.street},`,
            `${activation.city} ${activation.state} ${activation.zip}`,
            `${activation.userFirstName} ${activation.userLastName}`,
          ].join(' - '),
          value: activation.activationId.toString(),
          activation: activation,
        }));

        setActivationOptions(options);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _onSubmitForm = async (formValues) => {
    setIsSubmitting(true);
    setOrderResult(null);

    const orderPayload = buildPayload({
      formValues,
      automatedOrderPlacerType,
    });

    if (!orderPayload) {
      setIsSubmitting(false);
      global.alert('Invalid order payload');
      return;
    }

    const response = await dispatch(requestUtilityApi, {
      action: SR_API_ACTION_AUTOMATED_ORDER_PLACER_PLACE_ORDER,
      payload: {
        utilityActivationItemId: parseInt(formValues.utilityActivationItemId, 10),
        orderPayload,
      },
    });

    setOrderResult(response);
    setIsSubmitting(false);
  };

  const renderOrderResult = () => {
    if (!orderResult) {
      return null;
    }

    const STATUS_STYLES = {
      200: {
        backgroundColor: '#e6ffe6',
        borderColor: '#99ff99',
      },
      422: {
        backgroundColor: '#e6f3ff',
        borderColor: '#99ccff',
      },
      default: {
        backgroundColor: '#ffe6e6',
        borderColor: '#ff9999',
      },
    };
    const { backgroundColor, borderColor } =
      STATUS_STYLES[orderResult.status] || STATUS_STYLES.default;

    return (
      <SrBox
        marginTop={20}
        padding={16}
        backgroundColor={backgroundColor}
        borderRadius={4}
        borderWidth={1}
        borderStyle="solid"
        borderColor={borderColor}
      >
        <SrText font="whitney-sc" weight="bold" size="md" display="block">
          {orderResult.title}
        </SrText>
        <SrText font="whitney" size="sm" display="block" marginBottom={8}>
          {orderResult.detail}
        </SrText>
        <SrText font="whitney" size="sm" display="block">
          {orderResult.orderId && (
            <>
              External Order ID: {orderResult.orderId}
              <br />
            </>
          )}
          {orderResult.confirmationNumber && (
            <>
              Confirmation Number: {orderResult.confirmationNumber}
              <br />
            </>
          )}
          {orderResult.address && (
            <>
              Address: {orderResult.address}
              <br />
            </>
          )}
          {orderResult.availableDates && (
            <>
              Available dates: {orderResult.availableDates.join(', ')}
              <br />
            </>
          )}
        </SrText>
        <ImageGallery images={orderResult.screenshots} />
      </SrBox>
    );
  };

  const getActivationById = (activationId) => {
    const option = activationOptions.find((opt) => opt.value === activationId.toString());
    return option ? option.activation : null;
  };

  const handleSelectChange = (e, setFieldValue) => {
    const selectedActivationId = e.target.value;
    const selectedActivation = getActivationById(selectedActivationId);

    if (selectedActivation) {
      handleSelectedActivation({
        setFieldValue,
        selectedActivation,
        automatedOrderPlacerType,
      });

      const planInfo = [
        selectedActivation.providerName || 'Provider',
        selectedActivation.planTitle || null,
        selectedActivation.variantPrice
          ? `$${selectedActivation.variantPrice}`
          : `$${selectedActivation.planPrice}`,
        selectedActivation.planInternetSpeed
          ? `${selectedActivation.planInternetSpeed} Mbps`
          : null,
      ]
        .filter(Boolean)
        .join(' - ');

      setSelectedPlan(planInfo);
    }
  };

  const formFields = getFormFields({
    options: activationOptions,
    handleSelectChange: (e, setFieldValue) => handleSelectChange(e, setFieldValue),
    automatedOrderPlacerType,
  });

  const title = getTitle(automatedOrderPlacerType);

  return (
    <SrDarkModal>
      <SrBox className="closeIcon" onClick={onClose}>
        <SrIcon className="x-close-large-white" />
      </SrBox>
      <SrModalContent
        style={{
          maxWidth: 800,
          width: '100%',
          height: 'auto',
          marginTop: 90,
          overflow: 'visible',
        }}
      >
        <SrText font="whitney-sc" weight="bold" size="xl" display="block" align="center">
          {title}
        </SrText>

        {isLoading ? (
          <SrText font="whitney-sc" weight="bold" size="large" display="block" align="center">
            Loading...
          </SrText>
        ) : (
          <Formik
            initialValues={createInitialValues({ formFields })}
            validate={createFormValidationFn(formFields)}
            onSubmit={_onSubmitForm}
          >
            {(formikBag) => (
              <Form>
                {formFields.map((formField) => (
                  <React.Fragment key={formField.id}>
                    {formField.id in sectionTitles && (
                      <SrText
                        font="whitney-sc"
                        fontSize="20px"
                        weight="800"
                        display="block"
                        paddingTop="20px"
                        paddingBottom="8px"
                      >
                        {sectionTitles[formField.id]}
                      </SrText>
                    )}
                    <SrFormikField
                      formField={{
                        ...formField,
                        properties: {
                          ...formField.properties,
                          onChange: (e) => {
                            if (formField.type === 'select' && formField.properties.onChange) {
                              formField.properties.onChange(e, formikBag);
                            }
                            formikBag.handleChange(e);
                          },
                        },
                      }}
                      formikBag={formikBag}
                      className={SR_FIELD_THEME_ADMIN}
                    />
                    {formField.id === 'planId' && selectedPlan && (
                      <>
                        <SrText
                          color="green"
                          font="whitney-sc"
                          weight="bold"
                          size="large"
                          display="block"
                          paddingTop="20px"
                        >
                          {selectedPlan}
                        </SrText>
                        <br />
                      </>
                    )}
                  </React.Fragment>
                ))}
                {renderOrderResult()}
                <SrBox marginTop="20px">
                  <ButtonDeprecated
                    type="submit"
                    theme={BUTTON_THEME_YELLOW}
                    disabled={!formikBag.isValid || isSubmitting}
                    onClick={formikBag.handleSubmit || isSubmitting}
                  >
                    {isSubmitting ? 'Placing Order...' : 'Place Order'}
                  </ButtonDeprecated>
                </SrBox>
              </Form>
            )}
          </Formik>
        )}
      </SrModalContent>
    </SrDarkModal>
  );
};

UPOrderPlacingForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  automatedOrderPlacerType: PropTypes.string.isRequired,
};

UPOrderPlacingForm.displayName = 'UPOrderPlacingForm';

export default UPOrderPlacingForm;
